import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { extname } from '@common/util/path';
import { ApiService } from '@portal-core/auth/services/api.service';
import { defer, map, Observable } from 'rxjs';

/**
 *  Provides helper methods for working with images.
 */
@Service()
export class ImageService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  /**
   * Creates an `HTMLImageElement` and loads the given image path into it.
   * Emits the `HTMLImageElement` after it has loaded and completes the `Observable`.
   * */
  loadImage$(path: string): Observable<HTMLImageElement> {
    return new Observable(observer => {
      try {
        const img = new Image();

        img.onload = function () {
          observer.next(img);
          observer.complete();
        };

        img.onerror = function (error: Event | string) {
          observer.error(error);
          observer.complete();
        };

        img.src = path;
      } catch (ex) {
        observer.error(ex);
        observer.complete();
      }
    });
  }

  /** Returns the url for a `MadCapImage`. The url is an asset hosted by the Central API server. */
  getMadCapImageUrl(storageId: string, extension: string): string {
    return `${this.apiService.centralAssetsBaseUri}/${storageId}${extension}`;
  }

  /**
   * Infers the image MIME type from a file path or data URL.
   * Returns `null` if the MIME type cannot be determined.
   */
  getImageMime(pathOrDataUrl: string): string | null {
    // data URL: data:image/png;base64,...
    const dataUrlMatch = pathOrDataUrl.match(/^data:(image\/[a-z0-9.+-]+)/i);
    if (dataUrlMatch) {
      return dataUrlMatch[1].toLowerCase();
    }

    // file extension
    const ext = extname(pathOrDataUrl);
    if (ext) {
      switch (ext.toLowerCase()) {
        case '.png': return 'image/png';
        case '.jpg':
        case '.jpeg': return 'image/jpeg';
        case '.gif': return 'image/gif';
        case '.bmp': return 'image/bmp';
        case '.tif':
        case '.tiff': return 'image/tiff';
        case '.svg': return 'image/svg+xml';
      }
    }
    return null;
  }

  /**
   * Loads an image and reads in its dimensions.
   * Emits the dimensions and completes the `Observable`.
   */
  getImageDimensions$(path: string): Observable<{ height: number, width: number }> {
    // TIFF files cannot be loaded into an `HTMLImageElement`, so we need to fetch and parse the dimensions manually from the file header
    if (this.getImageMime(path) === 'image/tiff') {
      return this.getTiffDimensions$(path);
    }

    return this.loadImage$(path).pipe(
      map(img => {
        return {
          height: img.height,
          width: img.width
        };
      })
    );
  }

  fetchSvg$(path: string): Observable<SVGSVGElement> {
    return this.fetchXml$(path, 'image/svg+xml').pipe(
      map(xmlDoc => xmlDoc?.firstElementChild as SVGSVGElement)
    );
  }

  fetchXml$(path: string, headerType: 'application/xml' | 'image/svg+xml' = 'application/xml'): Observable<XMLDocument> {
    const headers = new HttpHeaders();
    headers.set('Accept', headerType);
    return this.http.get(path, { headers, responseType: 'text' }).pipe(
      map(response => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(response, headerType);
        if (xmlDoc.getElementsByTagName('parsererror').length !== 0) {
          // file is not a valid xml doc
          return null;
        }
        return xmlDoc as XMLDocument;
      })
    );
  }

  /** Gets the image resolution (DPI). */
  getDpi$(base64: string, mime: string): Observable<{ x: number; y: number } | null> {
    // fetch + arrayBuffer work off-thread, so we don't intercept the UI thread
    return defer(() => fetch(`data:${mime};base64,${base64}`)
      .then(r => r.arrayBuffer())
      .then(buf => {
        const bytes = new Uint8Array(buf);
        const dv = new DataView(buf);
        switch (mime?.toLowerCase()) {
          case 'image/png':
            return this.parsePngDpi(bytes, dv);
          case 'image/jpeg':
            return this.parseJpegDpi(bytes, dv);
          case 'image/bmp':
            return this.parseBmpDpi(bytes, dv);
          case 'image/tiff':
            return this.parseTiffDpi(bytes, dv);
        }
        return null;
      }));
  }

  /** Gets whether the given MIME type is an image format that can include DPI information.
   * GIF and SVG treated as resolution-independent */
  supportsDpi(mime: string): boolean {
    switch (mime?.toLowerCase()) {
      case 'image/png':
      case 'image/jpeg':
      case 'image/bmp':
      case 'image/tiff':
        return true;
    }
    return false;
  }

  /** Fetches a TIFF file and parses its dimensions from the IFD header. */
  private getTiffDimensions$(path: string): Observable<{ height: number, width: number }> {
    return defer(() => fetch(path)
      .then(r => r.arrayBuffer())
      .then(buf => {
        const bytes = new Uint8Array(buf);
        const dv = new DataView(buf);
        return this.parseTiffDimensions(bytes, dv);
      })
    );
  }

  /** Parses PNG data to extract DPI information. */
  private parsePngDpi(bytes: Uint8Array, dv: DataView): { x: number; y: number } | null {
    // Walk chunks starting at offset 8 (after 8-byte signature).
    // Need at least 8 bytes for the chunk's length + type fields.
    let p = 8;
    while (p + 8 <= bytes.length) {
      const len = dv.getUint32(p);
      const type = String.fromCharCode(...bytes.slice(p + 4, p + 8));
      if (type === 'pHYs') {
        // pHYs payload is 9 bytes: 4 (x) + 4 (y) + 1 (unit)
        if (p + 17 > bytes.length) {
          return null;
        } // truncated pHYs chunk
        const xPpu = dv.getUint32(p + 8);
        const yPpu = dv.getUint32(p + 12);
        const unit = bytes[p + 16];
        return unit === 1
          ? { x: Math.round(xPpu / 39.3701), y: Math.round(yPpu / 39.3701) }
          : null; // aspect ratio only
      }
      p += 12 + len; // length + type + data + CRC
    }
    return null;
  }

  /** Parses JPEG data to extract DPI information. */
  private parseJpegDpi(bytes: Uint8Array, dv: DataView): { x: number; y: number } | null {
    let jfif: { x: number; y: number } | null = null;
    let exif: { x: number; y: number } | null = null;

    // Scan APP markers
    let p = 2; // skip SOI (FFD8)
    while (p + 4 < bytes.length && bytes[p] === 0xff) {
      const marker = bytes[p + 1];
      if (marker === 0xd9 || marker === 0xda) {
        break;
      } // EOI or SOS — image data follows

      const segLen = dv.getUint16(p + 2);
      if (marker === 0xe0 && // APP0 — JFIF
        p + 16 <= bytes.length && // guard the JFIF field reads below against truncated segments
        String.fromCharCode(...bytes.slice(p + 4, p + 8)) === 'JFIF') {
        const units = bytes[p + 11];   // 0=aspect, 1=DPI, 2=DPcm
        const xDen = dv.getUint16(p + 12);
        const yDen = dv.getUint16(p + 14);
        if (units === 1) {
          jfif = { x: xDen, y: yDen };
        } else if (units === 2) {
          jfif = { x: Math.round(xDen * 2.54), y: Math.round(yDen * 2.54) };
        }
      } else if (marker === 0xe1 && // APP1 — EXIF
        String.fromCharCode(...bytes.slice(p + 4, p + 8)) === 'Exif' &&
        bytes[p + 8] === 0 && bytes[p + 9] === 0) {
        exif = this.parseExifDpi(bytes, dv, p + 10); // TIFF header starts after "Exif\0\0"
      }
      p += 2 + segLen;
    }
    // EXIF wins when present — it's what Photoshop, Windows Explorer, and most viewers read/write.
    // Many JPEGs leave JFIF at the 72/72 default even when the real resolution lives in EXIF.
    return exif ?? jfif ?? null;
  }

  /** Parses BMP data to extract DPI information. */
  private parseBmpDpi(bytes: Uint8Array, dv: DataView): { x: number; y: number } | null {
    // Sanity check: "BM" signature
    if (bytes.length < 46 || bytes[0] !== 0x42 || bytes[1] !== 0x4d) {
      return null;
    }

    // Confirm DIB header is BITMAPINFOHEADER or later (≥ 40 bytes).
    // The 12-byte BITMAPCOREHEADER has no DPI fields.
    const dibSize = dv.getUint32(14, /* littleEndian */ true);
    if (dibSize < 40) {
      return null;
    }

    const xPpm = dv.getInt32(38, true);
    const yPpm = dv.getInt32(42, true);
    if (xPpm === 0 && yPpm === 0) {
      return null;
    }  // unspecified

    return {
      x: Math.round(xPpm / 39.3701),
      y: Math.round(yPpm / 39.3701)
    };
  }

  /** Parses TIFF data to extract DPI information. */
  private parseTiffDpi(bytes: Uint8Array, dv: DataView): { x: number; y: number } | null {
    return this.parseExifDpi(bytes, dv, 0);
  }

  /** Parses EXIF data from a JPEG APP1 segment or a TIFF file to extract DPI information.
   * Returns null if the EXIF data is missing or malformed, or if it specifies an aspect ratio only. */
  private parseExifDpi(bytes: Uint8Array, dv: DataView, start: number): { x: number; y: number } | null {
    if (start + 8 > bytes.length) {
      return null;
    }

    let le: boolean;
    if (bytes[start] === 0x49 && bytes[start + 1] === 0x49) {
      le = true; // "II"
    } else if (bytes[start] === 0x4d && bytes[start + 1] === 0x4d) {
      le = false; // "MM"
    } else {
      return null;
    }

    // Magic must be 42 (classic TIFF). 43 = BigTIFF, not handled here.
    if (dv.getUint16(start + 2, le) !== 42) {
      return null;
    }

    // All offsets inside the EXIF block are relative to start, not the file.
    const ifd0 = start + dv.getUint32(start + 4, le);
    if (ifd0 + 2 > bytes.length) {
      return null;
    }

    const entryCount = dv.getUint16(ifd0, le);
    let xRes: number | null = null;
    let yRes: number | null = null;
    let unit = 2; // TIFF spec default: inch
    for (let i = 0; i < entryCount; i++) {
      const p = ifd0 + 2 + i * 12;
      const tag = dv.getUint16(p, le);
      if (tag === 282 || tag === 283) {
        // RATIONAL — 8-byte value, always at an offset (relative to start).
        const off = start + dv.getUint32(p + 8, le);
        if (off + 8 > bytes.length) {
          continue;
        }
        const num = dv.getUint32(off, le);
        const den = dv.getUint32(off + 4, le);
        if (den === 0) {
          continue;
        }
        const v = num / den;
        if (tag === 282) {
          xRes = v;
        } else {
          yRes = v;
        }
      } else if (tag === 296) {
        // SHORT fits inline; value is the first 2 bytes of the value field.
        unit = dv.getUint16(p + 8, le);
      }
    }

    if (xRes == null || yRes == null) {
      return null;
    }
    if (unit === 1) {
      return null;
    } // aspect ratio only — no absolute DPI

    const factor = unit === 3 ? 2.54 : 1; // cm → inch
    return { x: Math.round(xRes * factor), y: Math.round(yRes * factor) };
  }

  /** Parses TIFF IFD tags to get dimensions. */
  private parseTiffDimensions(bytes: Uint8Array, dv: DataView): { height: number, width: number } | null {
    if (bytes.length < 8) {
      return null;
    }

    let le: boolean;
    if (bytes[0] === 0x49 && bytes[1] === 0x49) {
      le = true; // "II" little-endian
    } else if (bytes[0] === 0x4d && bytes[1] === 0x4d) {
      le = false; // "MM" big-endian
    } else {
      return null;
    }

    // Magic must be 42 (classic TIFF)
    if (dv.getUint16(2, le) !== 42) {
      return null;
    }

    const ifd0 = dv.getUint32(4, le);
    if (ifd0 + 2 > bytes.length) {
      return null;
    }

    const entryCount = dv.getUint16(ifd0, le);
    let width: number | null = null;
    let height: number | null = null;

    for (let i = 0; i < entryCount; i++) {
      const p = ifd0 + 2 + i * 12;
      if (p + 12 > bytes.length) {
        break;
      }
      const tag = dv.getUint16(p, le);
      const type = dv.getUint16(p + 2, le);
      if (tag === 256 || tag === 257) {
        // SHORT (3) fits inline as uint16; LONG (4) fits inline as uint32
        const v = type === 3
          ? dv.getUint16(p + 8, le)
          : dv.getUint32(p + 8, le);
        if (tag === 256) {
          width = v;
        }
        else {height = v;}
      }
      if (width !== null && height !== null) {
        break;
      }
    }

    if (width == null || height == null) {
      return null;
    }

    return { height, width };
  }
}
